import { useCallback, useRef, useState } from "react";
import {
  applyMove, checkEnd, createBoard, legalMoves, nextTurn, score,
  type Board, type Disc, type Outcome, opponent,
} from "../othello.ts";
import type { MatchView } from "../useMatch.ts";

interface Options {
  localDisc: Disc;
  onEnd: (outcome: Outcome, localCount: number) => void;
}

/**
 * Same shape as useMatch (see MatchScreen's Board/Hud, both reused as-is),
 * but with no AI: the local player's moves go out over the network (caller's
 * job — see FriendMatch.tsx), and the peer's moves come in via `receiveMove`.
 * Othello has no hidden info, so a move is just replayed on both boards —
 * no seed/state-sync trickery needed.
 */
export function useFriendMatch({ localDisc, onEnd }: Options) {
  const remoteDisc = opponent(localDisc);

  const [, force] = useState(0);
  const rerender = () => force((n) => n + 1);

  const boardRef = useRef<Board>(createBoard());
  const turnRef = useRef<Disc | null>("dark"); // dark (host) always starts
  const moveNoRef = useRef(1);
  const lastFlippedRef = useRef<number[]>([]);
  const lastPlacedRef = useRef<number | null>(null);
  const passByRef = useRef<Disc | null>(null);
  const endedRef = useRef(false);
  const outcomeRef = useRef<Outcome | null>(null);

  const onEndRef = useRef(onEnd);
  onEndRef.current = onEnd;

  const commitMove = useCallback((move: number, who: Disc) => {
    const { board: next, flipped } = applyMove(boardRef.current, move, who);
    boardRef.current = next;
    lastPlacedRef.current = move;
    lastFlippedRef.current = flipped;
    moveNoRef.current += 1;

    const follow = nextTurn(next, who);
    passByRef.current = follow === who ? opponent(who) : null;

    const end = checkEnd(next);
    if (end.ended || follow === null) {
      endedRef.current = true;
      turnRef.current = null;
      const s = score(next);
      const decided: Outcome =
        end.outcome ??
        (s.dark === s.light ? "draw" : s.dark > s.light ? "dark" : "light");
      outcomeRef.current = decided;
      rerender();
      onEndRef.current(decided, localDisc === "dark" ? s.dark : s.light);
      return;
    }
    turnRef.current = follow;
    rerender();
  }, [localDisc]);

  /** The local player places at `cell`. Caller is responsible for also
   *  sending it to the peer (see FriendMatch.tsx). */
  const play = useCallback(
    (cell: number) => {
      if (endedRef.current || turnRef.current !== localDisc) return;
      if (!legalMoves(boardRef.current, localDisc).includes(cell)) return;
      commitMove(cell, localDisc);
    },
    [localDisc, commitMove],
  );

  /** A move arrived from the peer. */
  const receiveMove = useCallback(
    (cell: number) => {
      if (endedRef.current || turnRef.current !== remoteDisc) return;
      if (!legalMoves(boardRef.current, remoteDisc).includes(cell)) return;
      commitMove(cell, remoteDisc);
    },
    [remoteDisc, commitMove],
  );

  /** Fresh board for a rematch — dark (host) starts again. */
  const reset = useCallback(() => {
    boardRef.current = createBoard();
    turnRef.current = "dark";
    moveNoRef.current = 1;
    lastFlippedRef.current = [];
    lastPlacedRef.current = null;
    passByRef.current = null;
    endedRef.current = false;
    outcomeRef.current = null;
    rerender();
  }, []);

  const s = score(boardRef.current);
  const turn = turnRef.current;
  const view: MatchView = {
    board: boardRef.current,
    turn,
    playerDisc: localDisc,
    aiDisc: remoteDisc,
    moveNo: moveNoRef.current,
    legal: turn === localDisc ? legalMoves(boardRef.current, localDisc) : [],
    lastFlipped: lastFlippedRef.current,
    lastPlaced: lastPlacedRef.current,
    passBy: passByRef.current,
    ended: endedRef.current,
    outcome: outcomeRef.current,
    playerCount: localDisc === "dark" ? s.dark : s.light,
    aiCount: remoteDisc === "dark" ? s.dark : s.light,
    emptyCount: s.empty,
    canPlay: !endedRef.current && turn === localDisc,
  };

  return { view, play, receiveMove, reset };
}
